import Vue from "vue";
import AddressInputGroup from "../customer/AddressInputGroup.vue";
import ValidationService from "../../services/ValidationService";
import TranslationService from "../../services/TranslationService";
import { setBeforePlaceOrder } from "./BeforePlaceOrderHook";

const ApiService = require("../../services/ApiService");
const NotificationService = require("../../services/NotificationService");

/**
 * Theme checkout (maversandVuePlugin): address form directly in the page instead of the address modal.
 *
 * Shown as long as the customer has no billing address. The addresses are not saved by a separate button but
 * right before the order is placed (see BeforePlaceOrderHook / PlaceOrder.placeOrder):
 *   1. validate the form fields
 *   2. remember shipping profile, payment method and total
 *   3. create the addresses with the regular store actions and reload checkout + basket
 *   4. if anything of (2) has changed, stop and let the customer review it - the next click places the order
 * Once the addresses are saved, the default slot (regular address selects) is rendered instead of the form.
 */
export default Vue.component("checkout-address-form", {

    components:
    {
        AddressInputGroup
    },

    props:
    {
        template:
        {
            type: String,
            default: "#mv-co-address-form"
        },
        invoiceOptionalFields: Object,
        invoiceRequiredFields: Object,
        deliveryOptionalFields: Object,
        deliveryRequiredFields: Object,
        invoiceDefaultSalutation: String,
        deliveryDefaultSalutation: String,
        email: String
    },

    data()
    {
        return {
            showForm: false,
            isSaving: false,
            hasDeliveryAddress: false,
            billing: {},
            delivery: {},
            countryUpdate: null
        };
    },

    computed:
    {
        shippingCountryId()
        {
            return this.hasDeliveryAddress ? this.delivery.countryId : this.billing.countryId;
        },

        // billing addresses may be in any EU country (legal obligation), deliveries only to active shipping countries
        isBillingCountryShippable()
        {
            return this.isShippingCountry(this.billing.countryId);
        },

        noShippingHint()
        {
            return TranslationService.translate("maversandVuePlugin::Additional.mvCheckoutNoShippingToCountry", {
                country: this.$store.getters.getCountryName(this.billing.countryId)
            });
        }
    },

    created()
    {
        this.showForm = !this.$store.state.checkout.readOnly && !this.$store.state.address.billingAddressList.length;

        if (this.showForm)
        {
            this.billing = this.createEmptyAddress("billing_address", this.invoiceOptionalFields, this.invoiceDefaultSalutation);
            this.delivery = this.createEmptyAddress("delivery_address", this.deliveryOptionalFields, this.deliveryDefaultSalutation);
            setBeforePlaceOrder(this.beforePlaceOrder);
        }
    },

    beforeDestroy()
    {
        setBeforePlaceOrder(null);
    },

    methods:
    {
        // same defaults as AddressSelect.showAddModal()
        createEmptyAddress(fieldPrefix, optionalFields, defaultSalutation)
        {
            const countryId = this.$store.state.localization.shippingCountryId;
            const countryKey = countryId === 12 ? "gb" : "de";
            const address = { countryId, email: this.email };

            if ((optionalFields[countryKey] || []).includes(fieldPrefix + ".salutation"))
            {
                address.gender = defaultSalutation;
                address.showPickupStation = false;
            }

            return address;
        },

        setField(addressKey, { field, value })
        {
            this[addressKey] = Object.assign({}, this[addressKey], { [field]: value });

            if (["gender", "countryId", "showPickupStation"].includes(field))
            {
                ValidationService.unmarkAllFields(this.getForm(addressKey));
            }
        },

        getForm(addressKey)
        {
            return addressKey === "billing" ? this.$refs.billingForm : this.$refs.deliveryForm;
        },

        isShippingCountry(countryId)
        {
            return this.$store.state.localization.shippingCountries.some(country => country.id === countryId);
        },

        refreshCheckoutAndBasket()
        {
            return Promise.all([
                this.$store.dispatch("refreshBasket"),
                this.$store.dispatch("refreshCheckout")
            ]);
        },

        /**
         * Set the shipping country right away (like the country select in the header, but without reloading the
         * page), so shipping profiles, payment methods and totals are already correct before ordering.
         */
        updateShippingCountry(countryId)
        {
            if (!this.showForm || countryId === this.$store.state.localization.shippingCountryId || !this.isShippingCountry(countryId))
            {
                return;
            }

            this.$store.commit("setIsBasketLoading", true);

            const countryUpdate = new Promise(resolve => ApiService.post("/rest/io/shipping/country", { shippingCountryId: countryId }).always(resolve))
                .then(() => this.refreshCheckoutAndBasket())
                .catch(() => null)
                .then(() =>
                {
                    // only the latest update ends the loading state
                    if (this.countryUpdate === countryUpdate)
                    {
                        this.countryUpdate = null;
                        this.$store.commit("setIsBasketLoading", false);
                    }
                });

            this.countryUpdate = countryUpdate;
        },

        /**
         * Registered as BeforePlaceOrderHook while the form is shown.
         * @param {boolean} isCheckoutValid result of the regular checkout validation (gtc, shipping, payment, ...)
         * @returns {Promise<boolean>} true = place the order
         */
        beforePlaceOrder(isCheckoutValid)
        {
            if (!this.validateForms() || !isCheckoutValid)
            {
                return Promise.resolve(false);
            }

            if (!this.isShippingCountry(this.shippingCountryId))
            {
                NotificationService.error(TranslationService.translate("Ceres::Template.checkoutInvalidShippingCountry"));

                return Promise.resolve(false);
            }

            let snapshot;

            this.isSaving = true;

            return Promise.resolve(this.countryUpdate)
                .then(() =>
                {
                    snapshot = this.getCheckoutSnapshot();

                    return this.saveAddress("billing", "1");
                })
                .then(() => this.hasDeliveryAddress && this.saveAddress("delivery", "2"))
                .then(() =>
                {
                    // addresses are saved: show the regular address selects (they register their own validators)
                    this.showForm = false;
                    setBeforePlaceOrder(null);

                    return this.refreshCheckoutAndBasket().catch(() => null);
                })
                .then(() => this.$nextTick())
                .then(() =>
                {
                    const current = this.getCheckoutSnapshot();
                    const changedSections = Object.keys(snapshot).filter(selector => snapshot[selector] !== current[selector]);

                    if (changedSections.length)
                    {
                        this.showChanges(changedSections);
                    }

                    return !changedSections.length;
                })
                .catch(error =>
                {
                    // API errors are already shown by handleSaveError(), only log unexpected ones
                    if (error instanceof Error)
                    {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }

                    return false;
                })
                .then(proceed =>
                {
                    this.isSaving = false;

                    return proceed;
                });
        },

        validateForms()
        {
            const forms = this.hasDeliveryAddress ? [this.$refs.billingForm, this.$refs.deliveryForm] : [this.$refs.billingForm];
            const invalidFields = [].concat(...forms.map(form => ValidationService.getInvalidFields(form)));

            if (!invalidFields.length)
            {
                return true;
            }

            // field labels without the "*" of required fields, see CreateUpdateAddress.validate()
            const fieldNames = invalidFields
                .map(field => (field.lastElementChild ? field.lastElementChild.innerHTML.trim() : "").replace(/\*$/, ""))
                .filter((name, index, names) => name && names.indexOf(name) === index);

            ValidationService.markInvalidFields(invalidFields, "error");
            NotificationService.error(
                TranslationService.translate("Ceres::Template.checkoutCheckAddressFormFields", { fields: fieldNames.join(", ") })
            );
            this.scrollTo(invalidFields[0]);

            return false;
        },

        /**
         * Creates the address, or updates it if it was already saved in an earlier attempt
         * (e.g. billing address saved, delivery address failed) to avoid duplicates.
         */
        saveAddress(addressKey, addressType)
        {
            const address = Object.assign({}, this[addressKey]);

            return this.$store.dispatch(address.id ? "updateAddress" : "createAddress", { address, addressType })
                .then(
                    response =>
                    {
                        if (!address.id && response && response.id)
                        {
                            this.setField(addressKey, { field: "id", value: response.id });
                        }
                    },
                    error =>
                    {
                        this.handleSaveError(error, this.getForm(addressKey));

                        throw error;
                    });
        },

        // same handling as CreateUpdateAddress._handleError()/_handleValidationErrors()
        handleSaveError(error, form)
        {
            const apiError = error && error.error;

            if (apiError && apiError.code === 11)
            {
                NotificationService.error({ code: apiError.code, message: "" });
                window.location.reload();
            }
            else if (apiError && [210, 211].includes(apiError.code))
            {
                NotificationService.error({ code: apiError.code, message: apiError.message });
            }
            else if ((error && error.validation_errors) || apiError)
            {
                const validationErrors = error.validation_errors || apiError;

                ValidationService.markFailedValidationFields(form, validationErrors);
                NotificationService.error(Object.values(validationErrors).join("<br>"));
                this.scrollTo(form);
            }
        },

        // keys are the selectors of the checkout sections to highlight if the value changes
        getCheckoutSnapshot()
        {
            const state = this.$store.state;

            return {
                ".mv-co-shipping": state.checkout.shipping.shippingProfileId,
                ".mv-co-payment": state.checkout.payment.methodOfPaymentId,
                ".mv-co-finish": state.basket.data.basketAmount
            };
        },

        showChanges(selectors)
        {
            const sections = selectors.map(selector => document.querySelector(selector)).filter(section => section);

            sections.forEach(section =>
            {
                section.classList.add("mv-co-section--changed");
                section.addEventListener("click", () => section.classList.remove("mv-co-section--changed"), { once: true });
            });

            NotificationService.warn(TranslationService.translate("maversandVuePlugin::Additional.mvCheckoutReviewChanges"));
            this.scrollTo(sections[0]);
        },

        scrollTo(element)
        {
            if (element)
            {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    },

    watch:
    {
        shippingCountryId(countryId)
        {
            this.updateShippingCountry(countryId);
        },

        isBillingCountryShippable(isShippable)
        {
            if (!isShippable)
            {
                this.hasDeliveryAddress = true;
            }
        }
    }
});
